<template>
  <div class="contain callpop-register">
    <div class="callpop-register__head">
      <h3 class="callpop-register__title">{{ text.registerTitle }}</h3>
      <el-button size="mini" icon="el-icon-back" @click="goList">{{ text.registerGoList }}</el-button>
    </div>

    <el-steps :active="step" align-center finish-status="success" class="callpop-register__steps">
      <el-step :title="text.registerStep1" />
      <el-step :title="text.registerStep2" />
      <el-step :title="text.registerStep3" />
    </el-steps>

    <!-- ① 사업자번호 확인 ------------------------------------------------ -->
    <el-card v-if="step === 0" shadow="never">
      <el-form label-width="130px" size="small">
        <el-form-item :label="text.registerAgencyLabel">
          <!-- `preload` — 「후보가 하나면 자기 관리점 고정」을 진입 시점에 판정해야 한다 -->
          <AgencyPicker
            v-model="agencyId"
            class="callpop-register__agency"
            preload
            :placeholder="text.agencyLabel"
            @loaded="onAgenciesLoaded" />
          <div v-if="agencyFixed" class="callpop-register__hint">{{ text.registerAgencyFixed }}</div>
        </el-form-item>
        <el-form-item :label="text.busiNumLabel">
          <el-input
            v-keep-number-caret
            :value="bizNoFieldText(busiNum)"
            class="callpop-register__bizno"
            :placeholder="text.busiNumPlaceholder"
            maxlength="14"
            clearable
            @input="onBizNoInput"
            @keyup.enter.native="checkBizNo" />
          <el-button size="small" :loading="checking" :disabled="!agencyId" @click="checkBizNo">{{
            text.busiNumCheckAction
          }}</el-button>
        </el-form-item>
      </el-form>

      <template v-if="bizView">
        <p class="callpop-register__status">
          <el-tag v-if="bizChip" size="small" :type="bizChip.tagType">{{ bizChip.text }}</el-tag>
          <!-- `EXISTS_HIDDEN` 은 서버 문구가 전부다 — 건수도 이름도 화면이 지어내지 않는다 -->
          <span v-if="bizView.message" class="callpop-register__message">{{ bizView.message }}</span>
        </p>

        <p v-if="bizView.status === 'NEW'" class="callpop-register__note">{{ text.bizNewNote }}</p>

        <template v-if="bizView.stores.length > 0">
          <p class="callpop-register__note">{{ text.bizSiblingNote }}</p>
          <!-- 샵인샵 추가가 C 레벨을 한 단계 더 만들 수 있다는 사실(§1-2) — 실제로 만들어졌는지는 결과 화면의 계정 줄이 답한다 -->
          <p class="callpop-register__hint">{{ text.bizSiblingAccountNote }}</p>
          <el-table :data="bizView.stores" size="mini" border>
            <el-table-column :label="text.storeNmColumn" min-width="180">
              <template slot-scope="scope">
                {{ scope.row.storeNm }}
                <!-- 지워진 매장에는 형제를 붙일 수 없다 — 서버도 400 으로 막는다 -->
                <el-tooltip v-if="isInactive(scope.row)" placement="top" :content="text.storeInactiveTip">
                  <el-tag size="mini" type="danger">{{ text.storeInactive }}</el-tag>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="ID" width="130" prop="storeId"></el-table-column>
            <el-table-column :label="text.agencyLabel" min-width="150">
              <template slot-scope="scope">{{ siblingAgencyText(scope.row) }}</template>
            </el-table-column>
            <el-table-column :label="$t('관리')" width="140">
              <template slot-scope="scope">
                <el-button size="mini" type="text" :disabled="isInactive(scope.row)" @click="pickSibling(scope.row)">{{
                  text.bizSiblingAction
                }}</el-button>
              </template>
            </el-table-column>
          </el-table>
        </template>

        <div class="callpop-register__footer">
          <el-button v-if="bizView.canProceed" size="small" type="primary" @click="startNew">{{
            text.confirmAction
          }}</el-button>
        </div>
      </template>
    </el-card>

    <!-- ② 매장 정보 ------------------------------------------------------ -->
    <el-card v-if="step === 1" shadow="never">
      <el-form label-width="130px" size="small">
        <el-form-item :label="text.busiNumLabel">
          <el-input :value="bizNoFieldText(form.busiNum)" class="callpop-register__bizno" disabled />
          <span v-if="sibling" class="callpop-register__hint">{{ text.bizSiblingPicked }}: {{ sibling.storeNm }}</span>
        </el-form-item>
        <el-form-item :label="text.ceoNmLabel" :error="formError.ceoNm">
          <el-input v-model="form.ceoNm" class="callpop-register__field" maxlength="30" clearable />
        </el-form-item>
        <el-form-item :label="text.custNmLabel" :error="formError.custNm">
          <el-input v-model="form.custNm" class="callpop-register__field" maxlength="60" clearable />
        </el-form-item>
        <el-form-item :label="text.storeNmLabel" :error="formError.storeNm">
          <el-input v-model="form.storeNm" class="callpop-register__field" maxlength="60" clearable />
        </el-form-item>
        <el-form-item :label="text.telLabel" :error="formError.tel">
          <el-input
            v-keep-number-caret
            :value="util.telFieldText(form.tel)"
            class="callpop-register__field"
            maxlength="20"
            clearable
            @input="form.tel = util.telDigits($event)" />
        </el-form-item>
        <el-form-item :label="text.addrLabel" :error="formError.storeAddr">
          <el-input v-model="form.storeAddr" class="callpop-register__field" readonly />
          <el-button size="small" @click="pickStoreAddress">{{ text.addrPickAction }}</el-button>
          <div v-if="addrMessage" class="callpop-register__hint">{{ addrMessage }}</div>
        </el-form-item>
        <el-form-item :label="text.addrDtlLabel">
          <el-input v-model="form.storeAddrDtl" class="callpop-register__field" maxlength="60" clearable />
        </el-form-item>
        <!-- 업종 코드표는 `GET /code/getCode/*` 다 — 그 액션이 없으면 **칸을 그리지 않는다**(401 함정) -->
        <el-form-item v-if="canReadCode" :label="text.categoryLabel">
          <el-select v-model="form.categoryCode" class="callpop-register__field" clearable filterable>
            <el-option v-for="row in categories" :key="row.value" :label="row.label" :value="row.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="text.busiEmailLabel" :error="formError.busiEmail">
          <el-input v-model="form.busiEmail" class="callpop-register__field" maxlength="60" clearable />
        </el-form-item>
      </el-form>

      <p v-if="serverMessage" class="callpop-register__error">{{ serverMessage }}</p>

      <div class="callpop-register__footer">
        <el-button size="small" @click="step = 0">{{ text.cancelAction }}</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="register">{{ text.registerAction }}</el-button>
      </div>
    </el-card>

    <!-- ③ 등록 결과 ------------------------------------------------------ -->
    <el-card v-if="step === 2" shadow="never">
      <el-alert :title="text.registerDone" type="success" :closable="false" show-icon />

      <el-descriptions class="callpop-register__result" :column="1" border size="small">
        <el-descriptions-item :label="text.resultStoreIdLabel">{{ result.storeId }}</el-descriptions-item>
        <el-descriptions-item v-if="result.custId" :label="text.resultCustIdLabel">{{
          result.custId
        }}</el-descriptions-item>
        <el-descriptions-item :label="text.resultMappingLabel">{{ mappingText }}</el-descriptions-item>
        <el-descriptions-item :label="text.resultParticipationLabel">{{ participationText }}</el-descriptions-item>
      </el-descriptions>

      <!-- 초기 비밀번호는 이 응답에 **한 번뿐**이다(§3-8) — 그 사실을 먼저 말하고 복사 버튼을 준다 -->
      <el-table :data="accounts" size="mini" border class="callpop-register__accounts">
        <el-table-column :label="$t('구분')" width="140" prop="label"></el-table-column>
        <el-table-column :label="$t('계정 ID')" min-width="150" prop="accountId"></el-table-column>
        <el-table-column :label="text.resultPasswordLabel" min-width="180">
          <template slot-scope="scope">
            <span class="callpop-register__password">{{ scope.row.password || '-' }}</span>
            <el-button v-if="scope.row.password" size="mini" type="text" @click="copy(scope.row.password)">{{
              text.copyAction
            }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <p class="callpop-register__warn">{{ text.resultPasswordOnce }} {{ text.resultPasswordChange }}</p>

      <div class="callpop-register__footer">
        <el-button size="small" @click="restart">{{ text.registerAgainAction }}</el-button>
        <el-button size="small" type="primary" @click="goList">{{ text.registerGoList }}</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import AgencyPicker from '@/components/callpop/AgencyPicker.vue'
import { HttpError, mergeErrors } from '@/plugins/http'
import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { httpErrorBody, httpErrorMessage, httpStatusOf } from '@/plugins/http/policy'
import { ADDRESS_PICKER_TEXT, pickAddress } from '@/utils/addressPicker'
import {
  BIZ_NO_CONFLICT,
  CALLPOP_ACTION,
  CALLPOP_API,
  CALLPOP_FORM_WRITE_CONFIG,
  CALLPOP_PATH,
  REGISTER_MODE,
  agencyStoresUrl,
  autoJoinText,
  bizNoCheckView,
  bizNoConflictView,
  bizNoStatusChip,
  isAccountIdConflict,
  isInactiveStore,
  orgLabel,
  registerResultAccounts,
  storeOwnerView,
  storeRegisterBody,
} from '@/utils/callpopOrg'
import { bizNoFieldText, bizNoInputValue } from '@/utils/bizNumber'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { copyText } from '@/utils/clipboard'
import { latestGuard } from '@/utils/latestGuard'
import mainVueMixin from '@/mixins/mainVueMixin'

const BIZ_NO_READ = { action: CALLPOP_ACTION.BIZ_NO_CHECK, url: `${CALLPOP_API}/bizno/check`, showLoading: false }
/**
 * 업종 코드표 — **매장 폼이 쓰는 그 표**(`sys_code` `STORE_TP`)다. 화면에 목록을 두지 않는다.
 *
 * 액션(`GetCode`)이 없는 역할에서는 **부르지 않고 칸도 그리지 않는다** — 리소스 없는 호출은 401 이고
 * 그 401 이 세션을 끊는다. 지금 `operatorMng`·`agencyMng` 에는 그 행이 없다(보고서 nav 표에 올렸다).
 * 업종은 매장 설정에서 나중에 채울 수 있는 값이라 칸이 없어도 등록은 끝난다(서버가 `null` 을 받는다).
 */
const CODE_READ = { action: 'GetCode', url: '/code/getCode/STORE_TP', showLoading: false }

/**
 * **신규 매장 등록** (화면 #3, `/v/callpop/stores/new`) — 3단계.
 *
 * ① 사업자번호 조회(§3-7)가 갈래를 정한다: `NEW` 는 바로 진행, `EXISTS_VISIBLE` 은 목록에서 형제 매장을
 * 골라 `mode=SIBLING`, `EXISTS_HIDDEN` 은 **서버 문구만** 보이고 진행할 수 없다(콜팝 밖 매장 정보를
 * 화면이 추정하지 않는다), `INVALID` 는 사유를 말한다.
 *
 * ② 사업자·매장 정보. 주소는 공용 선택기(`utils/addressPicker`)가 답하고 좌표는 못 얻어도 진행한다.
 *
 * ③ 결과 카드. **초기 비밀번호는 이 응답에 한 번뿐**이라 복사 버튼과 경고를 함께 둔다.
 *
 * 「자기 관리점 고정」은 역할이 아니라 **서버 범위**로 판정한다 — 관리점 계정에게는 `agencies/search` 가
 * 자기 1행만 주므로 후보가 하나면 고정이다(§3-1).
 */
export default {
  name: 'CallpopStoreRegisterView',

  mixins: [mainVueMixin],

  components: { AgencyPicker },

  data() {
    return {
      text: CALLPOP_TEXT,
      step: 0,
      agencyId: '',
      agencyFixed: false,
      busiNum: '',
      checking: false,
      /**
       * 사업자번호 확인의 **스냅샷** — `{bizNo, view}`. 확인을 누른 그 번호와 그 답을 함께 들고 다닌다.
       *
       * 종전에는 답만 들고 있어서, 확인 뒤 번호를 고치면 **새 번호에 옛 답**이 붙었다(2단계로 넘어가면
       * `form.busiNum` 은 새 번호인데 갈래는 옛 답이 정한 것이다). 번호가 달라지면 답은 무효다.
       */
      bizCheck: null,
      sibling: null,
      /** 늦게 온 확인 응답이 최신 번호의 답을 덮지 못하게(`utils/latestGuard`) */
      bizGuard: latestGuard(),
      /** 관리점 후보를 **처음** 읽었는가 — 「자기 관리점 고정」은 그 회차만 판정한다 */
      agencyLoaded: false,
      form: this.initForm(),
      formError: this.initFormError(),
      categories: [],
      addrMessage: '',
      serverMessage: '',
      saving: false,
      result: {},
    }
  },

  computed: {
    canReadCode() {
      return canRead(CODE_READ)
    },
    /**
     * 지금 화면에 보이는 확인 결과 — **입력한 번호와 스냅샷의 번호가 같을 때만** 있다.
     *
     * 사람이 번호를 고치는 순간 옛 답은 이 화면에서 사라진다. 갈래를 정하는 자리([startNew]·
     * [pickSibling])도 같은 값을 읽으므로 「보이는 답」과 「쓰는 답」이 갈라질 수 없다.
     */
    bizView() {
      const snapshot = this.bizCheck

      return snapshot && snapshot.bizNo === String(this.busiNum || '').trim() ? snapshot.view : null
    },
    bizChip() {
      return bizNoStatusChip(this.bizView && this.bizView.status)
    },
    accounts() {
      return registerResultAccounts(this.result)
    },
    mappingText() {
      const owner = storeOwnerView(this.result.mapping)

      return [owner.operatorText, owner.agencyText].filter(Boolean).join(' · ')
    },
    participationText() {
      return autoJoinText(this.result.autoJoin) || '-'
    },
  },

  created() {
    if (this.canReadCode) this.loadCategories()
  },

  beforeDestroy() {
    this.bizGuard.close()
  },

  methods: {
    /** 사업자번호 칸 — 치는 대로 000-00-00000 으로 보이고, 값은 숫자만이다(`utils/bizNumber` · 서버도 숫자만 저장) */
    onBizNoInput(value) {
      this.busiNum = bizNoInputValue(value)
    },
    bizNoFieldText,
    initForm() {
      return {
        mode: REGISTER_MODE.NEW,
        busiNum: '',
        ceoNm: '',
        custNm: '',
        storeNm: '',
        tel: '',
        storeAddr: '',
        storeAddrDtl: '',
        zipCd: '',
        lat: null,
        lng: null,
        categoryCode: '',
        busiEmail: '',
        siblingOf: null,
      }
    },

    initFormError() {
      return { ceoNm: '', custNm: '', storeNm: '', tel: '', storeAddr: '', busiEmail: '' }
    },

    /**
     * 후보가 하나뿐이면 그 관리점으로 고정이다 — 관리점 계정에게 서버가 자기 1행만 준다.
     *
     * **첫 회차만 본다.** 선택기는 사람이 친 질의어로도 후보를 다시 읽는데, 그 결과가 우연히 한 행이면
     * (이름을 거의 다 친 경우) 「자기 관리점 고정」으로 잘못 읽힌다. 범위를 말해 주는 것은 **질의어 없는
     * 첫 조회**뿐이다.
     */
    onAgenciesLoaded(options) {
      if (this.agencyLoaded) return
      this.agencyLoaded = true

      if (options.length !== 1) return

      this.agencyFixed = true
      if (!this.agencyId) this.agencyId = options[0].id
    },

    async loadCategories() {
      try {
        const outcome = await permittedRead(CODE_READ)
        const rows = (outcome.allowed && outcome.data) || []

        this.categories = (Array.isArray(rows) ? rows : []).map((row) => ({
          label: row.nmLan || row.nm,
          value: row.val,
        }))
      } catch (error) {
        this.categories = []
      }
    },

    /** 지워진 매장인가 — 목록 화면과 **같은 술어**다 */
    isInactive(row) {
      return isInactiveStore(row)
    },

    siblingAgencyText(row) {
      const agency = row && row.agency

      return agency ? orgLabel(agency.name || agency.agencyNm, agency.code) : this.text.mappingNone
    },

    async checkBizNo() {
      const bizNo = String(this.busiNum || '').trim()
      const seq = this.bizGuard.begin()

      this.bizCheck = null
      this.sibling = null
      this.checking = true

      try {
        const outcome = await permittedRead(Object.assign({}, BIZ_NO_READ, { params: { busiNum: bizNo } }))
        if (!this.bizGuard.isLatest(seq)) return

        // 답과 **그 답이 답한 번호**를 함께 둔다 — 번호가 바뀌면 [bizView] 가 스스로 사라진다
        this.bizCheck = outcome.allowed ? { bizNo, view: bizNoCheckView(outcome.data) } : null
      } catch (error) {
        if (this.bizGuard.isLatest(seq)) this.bizCheck = null
      } finally {
        if (this.bizGuard.isLatest(seq)) this.checking = false
      }
    },

    /** 2단계로 넘어가는 두 자리는 **스냅샷의 번호**를 쓴다 — 화면의 입력이 그 사이 바뀌었을 수 있다 */
    startNew() {
      if (!this.bizView) return

      this.toStepTwo({ mode: REGISTER_MODE.NEW })
    },

    pickSibling(row) {
      if (!this.bizView || this.isInactive(row)) return

      this.sibling = row
      this.toStepTwo({ mode: REGISTER_MODE.SIBLING, siblingOf: row.storeCd })
    },

    toStepTwo(over) {
      this.form = Object.assign(this.initForm(), { busiNum: this.bizCheck.bizNo }, over)
      this.formError = this.initFormError()
      this.serverMessage = ''
      this.step = 1
    },

    /**
     * 주소 선택 — 위젯·지오코더를 만지는 자리는 `utils/addressPicker` 하나다(B7-a).
     * 좌표를 못 얻어도 고른 주소를 버리지 않는다(서버가 채운다).
     */
    async pickStoreAddress() {
      this.addrMessage = ''

      try {
        const picked = await pickAddress({ geocode: true })
        if (!picked) return

        this.form = Object.assign({}, this.form, {
          storeAddr: picked.address,
          zipCd: picked.zonecode,
          lat: picked.lat,
          lng: picked.lng,
        })
      } catch (error) {
        this.addrMessage = ADDRESS_PICKER_TEXT.widgetMissing
      }
    },

    async register() {
      if (!this.agencyId) return

      this.formError = this.initFormError()
      this.serverMessage = ''
      this.saving = true

      try {
        const response = await this.$axios.post(
          agencyStoresUrl(this.agencyId),
          storeRegisterBody(this.form),
          CALLPOP_FORM_WRITE_CONFIG
        )

        this.result = response.data || {}
        this.step = 2
      } catch (error) {
        if (error instanceof HttpError && error.messages) mergeErrors(this.formError, error.messages)
        this.serverMessage = isAccountIdConflict(httpStatusOf(error), httpErrorBody(error))
          ? this.text.accountIdConflict
          : httpErrorMessage(error, this.text.requiredFieldMissing)
        this.applyBizNoConflict(error)
      } finally {
        this.saving = false
      }
    },

    /**
     * **저장이 사업자번호 때문에 막혔다**(409) — 1단계의 답으로 되돌린다.
     *
     * 그 사이 누군가 같은 사업자로 매장을 만들면, 1단계에서 `NEW` 였던 번호가 저장 시점에는 형제를
     * 요구한다(`SIBLING_REQUIRED`, 본문에 후보 목록). 화면이 그것을 문장으로만 받으면 사람은 2단계에
     * 갇힌 채 같은 저장을 되풀이한다 — **서버가 준 새 답으로 1단계를 다시 세우는 것**이 답이다.
     * 가시 범위 밖(`BIZNO_EXISTS_HIDDEN`)이면 후보가 없으므로 서버 문구만 남는다.
     */
    applyBizNoConflict(error) {
      if (httpStatusOf(error) !== 409) return

      const view = bizNoConflictView(httpErrorBody(error), this.bizCheck && this.bizCheck.bizNo)
      if (!view) return

      this.bizCheck = { bizNo: view.bizNo, view: view.result }
      this.sibling = null
      this.step = 0
    },

    copy(value) {
      return copyText(value).then((copied) =>
        copied ? this.$message.success(this.text.copied) : this.$message.warning(this.text.copyFailed)
      )
    },

    restart() {
      this.step = 0
      this.busiNum = ''
      this.bizCheck = null
      this.sibling = null
      this.form = this.initForm()
      this.result = {}
    },

    goList() {
      this.$router.push({ path: CALLPOP_PATH.STORES })
    },
  },
}
</script>

<style scoped>
.callpop-register__head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}
.callpop-register__title {
  font-size: 16px;
  margin: 0;
}
.callpop-register__steps {
  margin-bottom: 16px;
}
.callpop-register__agency,
.callpop-register__field {
  display: inline-block;
  width: 320px;
}
.callpop-register__bizno {
  display: inline-block;
  margin-right: 8px;
  width: 200px;
}
.callpop-register__hint,
.callpop-register__note {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}
.callpop-register__status {
  margin: 12px 0 4px;
}
.callpop-register__message {
  margin-left: 8px;
}
.callpop-register__footer {
  margin-top: 16px;
  text-align: right;
}
.callpop-register__result,
.callpop-register__accounts {
  margin-top: 12px;
}
.callpop-register__password {
  font-family: monospace;
  margin-right: 8px;
}
.callpop-register__warn {
  color: #e6a23c;
  font-size: 12px;
  margin: 8px 0 0;
}
.callpop-register__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 8px 0 0;
}
</style>
